import { getValueFormat } from '@baurine/grafana-value-formats'
import type { TimeRange } from '@tidbcloud/uikit/biz'

import { TOP_SQL_OTHERS_LABEL } from '~/pages/cluster/top-ru/display'
import { ClusterDeployTypeV2 } from '~/utils/cluster-status'

import type {
  StatementV2Dimension,
  StatementV2Metric,
  StatementV2Model,
  StatementV2SummaryItem,
  StatementV2TopStatsItem
} from './types'

export type StatementMetricKey = StatementV2Metric
export type StatementSummaryDimension = StatementV2Dimension

export interface StatementContributorItem {
  key: string
  label: string
  value: number
}

export interface StatementContributorSummary {
  totalCount: number
  totalValue: number
  items: StatementContributorItem[]
}

export interface StatementTrendPoint {
  timestamp: number
  value: number
}

export interface StatementTrendSeries {
  key: string
  label: string
  points: StatementTrendPoint[]
}

export interface NormalizedStatementSummaryResponse {
  items: StatementV2SummaryItem[]
  step?: number
}

export const DEFAULT_STATEMENT_TREND_CHART_WIDTH = 720
export const STATEMENT_V2_DEFAULT_TIME_RANGE: TimeRange = {
  type: 'relative',
  value: 2 * 60 * 60
}
const TREND_BAR_WIDTH_PX = 6
const MIN_TREND_STEP_SECONDS = 1
const SUMMARY_STEP_CANDIDATES = [
  1,
  2,
  5,
  10,
  15,
  30,
  60,
  2 * 60,
  5 * 60,
  10 * 60,
  15 * 60,
  30 * 60,
  60 * 60,
  2 * 60 * 60,
  3 * 60 * 60,
  6 * 60 * 60,
  12 * 60 * 60,
  24 * 60 * 60
] as const
const UNKNOWN_USER_LABEL = 'Unknown User'
const UNKNOWN_DB_LABEL = 'Unknown DB'
const UNKNOWN_TABLE_LABEL = 'Unknown Table'
const UNKNOWN_TYPE_LABEL = 'Unknown Type'
const EVICTED_STATEMENT_LABEL = 'Evicted Statement'

export const STATEMENT_V2_FIELDS =
  'digest,digest_text,schema_name,table_names,stmt_type,user,summary_begin_time,summary_end_time,sum_latency,avg_latency,max_latency,min_latency,exec_count,plan_count,sum_ru,avg_ru'

export function formatStatementUserLabel(user?: string, stripPrefix = false) {
  if (!user) {
    return UNKNOWN_USER_LABEL
  }
  if (!stripPrefix) {
    return user
  }

  const parts = user.split('.')
  return parts.length > 1 ? parts.slice(1).join('.') : user
}

export const isStatementRUMetricAvailable = (available: boolean) => available

export const isStatementV2RUMetricAvailable = (deployTypeV2?: string) =>
  deployTypeV2 === ClusterDeployTypeV2.Premium ||
  deployTypeV2 === ClusterDeployTypeV2.EssentialV2

export function getStatementMetricOptions(ruMetricAvailable: boolean) {
  const options: Array<{ value: StatementMetricKey; label: string }> = [
    { value: 'sum_latency', label: 'Total Latency' },
    { value: 'avg_latency', label: 'Mean Latency' },
    { value: 'exec_count', label: 'Executions Count' },
    { value: 'plan_count', label: 'Plans Count' }
  ]

  if (isStatementRUMetricAvailable(ruMetricAvailable)) {
    options.unshift(
      { value: 'sum_ru', label: 'Total RU' },
      { value: 'avg_ru', label: 'Mean RU' }
    )
  }
  return options
}

export const getDefaultStatementMetric = (
  ruMetricAvailable: boolean
): StatementMetricKey =>
  isStatementRUMetricAvailable(ruMetricAvailable) ? 'sum_ru' : 'sum_latency'

export function formatStatementMetricValue(
  metric: StatementMetricKey,
  value: number
) {
  switch (metric) {
    case 'sum_ru':
    case 'avg_ru':
      return getValueFormat('short')(value, 2, 2)
    case 'sum_latency':
    case 'avg_latency':
      return getValueFormat('ns')(value, 1)
    case 'exec_count':
      return getValueFormat('short')(value, 0, 1)
    case 'plan_count':
      return getValueFormat('short')(value, 0, 0)
  }
}

export const getStatementRecordKey = (row: StatementV2Model) =>
  `${row.digest || row.digest_text || '-'}:${row.schema_name || '-'}:${
    row.plan_digest || '-'
  }`

export const getStatementSeriesKey = (
  row: Pick<StatementV2Model, 'digest' | 'digest_text'>
) => row.digest || row.digest_text || EVICTED_STATEMENT_LABEL

export const getStatementDisplayName = (row: StatementV2Model) =>
  row.digest_text?.trim() || row.digest?.trim() || EVICTED_STATEMENT_LABEL

function aggregateStatementRow(
  base: StatementV2Model,
  incoming: StatementV2Model
) {
  base.sum_latency = (base.sum_latency ?? 0) + (incoming.sum_latency ?? 0)
  base.sum_ru = (base.sum_ru ?? 0) + (incoming.sum_ru ?? 0)
  base.exec_count = (base.exec_count ?? 0) + (incoming.exec_count ?? 0)
  base.plan_count = Math.max(base.plan_count ?? 0, incoming.plan_count ?? 0)
  base.max_latency = Math.max(base.max_latency ?? 0, incoming.max_latency ?? 0)
  base.min_latency =
    base.min_latency == null
      ? incoming.min_latency
      : incoming.min_latency == null
      ? base.min_latency
      : Math.min(base.min_latency, incoming.min_latency)
  base.first_seen =
    base.first_seen == null
      ? incoming.first_seen
      : incoming.first_seen == null
      ? base.first_seen
      : Math.min(base.first_seen, incoming.first_seen)
  base.last_seen =
    base.last_seen == null
      ? incoming.last_seen
      : incoming.last_seen == null
      ? base.last_seen
      : Math.max(base.last_seen, incoming.last_seen)
  base.summary_begin_time =
    base.summary_begin_time == null
      ? incoming.summary_begin_time
      : incoming.summary_begin_time == null
      ? base.summary_begin_time
      : Math.min(base.summary_begin_time, incoming.summary_begin_time)
  base.summary_end_time =
    base.summary_end_time == null
      ? incoming.summary_end_time
      : incoming.summary_end_time == null
      ? base.summary_end_time
      : Math.max(base.summary_end_time, incoming.summary_end_time)
  base.avg_latency = base.exec_count
    ? (base.sum_latency ?? 0) / base.exec_count
    : 0
  base.avg_ru = base.exec_count ? (base.sum_ru ?? 0) / base.exec_count : 0
  base.digest ||= incoming.digest
  base.digest_text ||= incoming.digest_text
  base.schema_name ||= incoming.schema_name
  base.user ||= incoming.user
  base.sample_user ||= incoming.sample_user
  base.stmt_type ||= incoming.stmt_type
  base.plan_digest ||= incoming.plan_digest
  return base
}

export function aggregateStatementsForTable(rows: StatementV2Model[]) {
  const rowMap = new Map<string, StatementV2Model>()

  rows.forEach((row) => {
    const key = getStatementRecordKey(row)
    const existing = rowMap.get(key)
    if (!existing) {
      rowMap.set(key, {
        ...row,
        avg_latency: row.exec_count
          ? (row.sum_latency ?? 0) / row.exec_count
          : row.avg_latency ?? 0,
        avg_ru: row.exec_count
          ? (row.sum_ru ?? 0) / row.exec_count
          : row.avg_ru ?? 0
      })
      return
    }
    rowMap.set(key, aggregateStatementRow(existing, row))
  })

  return [...rowMap.values()]
}

function getTopstatsItemLabel(
  item: StatementV2TopStatsItem,
  dimension: StatementV2Dimension,
  stripUserPrefix = false
) {
  switch (dimension) {
    case 'user':
      return formatStatementUserLabel(item.user || item.key, stripUserPrefix)
    case 'db':
      return (
        item.schema_name ||
        item.db ||
        item.database ||
        item.key ||
        UNKNOWN_DB_LABEL
      )
    case 'table':
      return item.table_name || item.table || item.key || UNKNOWN_TABLE_LABEL
    case 'sqltypes':
      return item.stmt_type || item.key || UNKNOWN_TYPE_LABEL
    case 'digest':
      return (
        item.query_sample_text?.trim() ||
        item.digest_text?.trim() ||
        item.sql_digest ||
        EVICTED_STATEMENT_LABEL
      )
  }
}

function getTopstatsItemKey(
  item: StatementV2TopStatsItem,
  dimension: StatementV2Dimension
) {
  switch (dimension) {
    case 'user':
      return item.user || item.key || '__unknown_user__'
    case 'db':
      return (
        item.schema_name ||
        item.db ||
        item.database ||
        item.key ||
        '__unknown_db__'
      )
    case 'table':
      return item.table_name || item.table || item.key || '__unknown_table__'
    case 'sqltypes':
      return item.stmt_type || item.key || '__unknown_type__'
    case 'digest':
      return (
        item.sql_digest || item.key || getTopstatsItemLabel(item, dimension)
      )
  }
}

function getTopstatsItemValue(
  item: StatementV2TopStatsItem,
  metric: StatementMetricKey
) {
  if (item.value != null) {
    return item.value
  }

  switch (metric) {
    case 'sum_ru':
      return item.total_ru ?? 0
    case 'avg_ru':
      return item.avg_ru ?? 0
    case 'sum_latency':
      return item.sum_latency ?? 0
    case 'avg_latency':
      return item.avg_latency ?? 0
    case 'exec_count':
      return item.exec_count ?? 0
    case 'plan_count':
      return item.plan_count ?? 0
  }
}

export function buildStatementContributorSummaryFromTopstats(
  metric: StatementMetricKey,
  dimension: StatementV2Dimension,
  items: StatementV2TopStatsItem[],
  stripUserPrefix = false
): StatementContributorSummary {
  const contributorItems = items.map((item) => ({
    key: getTopstatsItemKey(item, dimension),
    label: getTopstatsItemLabel(item, dimension, stripUserPrefix),
    value: getTopstatsItemValue(item, metric)
  }))

  return {
    totalCount: contributorItems.length,
    totalValue: contributorItems.reduce((sum, item) => sum + item.value, 0),
    items: contributorItems.slice(0, 10)
  }
}

export function normalizeStatementSummaryResponse(
  payload: unknown
): NormalizedStatementSummaryResponse {
  if (Array.isArray(payload)) {
    return { items: payload as StatementV2SummaryItem[] }
  }
  if (!payload || typeof payload !== 'object') {
    return { items: [] }
  }

  const envelope = payload as {
    data?:
      | StatementV2SummaryItem[]
      | { data?: StatementV2SummaryItem[]; step?: number }
    step?: number
  }
  if (Array.isArray(envelope.data)) {
    return { items: envelope.data, step: envelope.step }
  }
  if (
    envelope.data &&
    typeof envelope.data === 'object' &&
    Array.isArray(envelope.data.data)
  ) {
    return {
      items: envelope.data.data,
      step: envelope.step ?? envelope.data.step
    }
  }
  return { items: [], step: envelope.step }
}

export function fillTrendPointGaps(
  points: StatementTrendPoint[],
  stepSeconds: number,
  timeRangeValue: [number, number]
) {
  if (!points.length || stepSeconds <= 0) {
    return points
  }

  const sorted = [...points].sort(
    (left, right) => left.timestamp - right.timestamp
  )
  const values = new Map(sorted.map((point) => [point.timestamp, point.value]))
  let start = sorted[0].timestamp
  let end = sorted[sorted.length - 1].timestamp
  while (start - stepSeconds >= timeRangeValue[0]) start -= stepSeconds
  while (end + stepSeconds <= timeRangeValue[1]) end += stepSeconds

  const filled: StatementTrendPoint[] = []
  for (let timestamp = start; timestamp <= end; timestamp += stepSeconds) {
    filled.push({ timestamp, value: values.get(timestamp) ?? 0 })
  }
  return filled
}

export function getStatementSummaryStepSeconds(
  timeRangeSeconds: number,
  chartWidth: number
) {
  const safeRange = Math.max(MIN_TREND_STEP_SECONDS, timeRangeSeconds)
  const safeWidth = Math.max(
    chartWidth || DEFAULT_STATEMENT_TREND_CHART_WIDTH,
    TREND_BAR_WIDTH_PX
  )
  const rawStep = Math.ceil(
    safeRange / Math.max(1, Math.floor(safeWidth / TREND_BAR_WIDTH_PX))
  )
  return SUMMARY_STEP_CANDIDATES.find((step) => step >= rawStep) || rawStep
}

function getSummaryItemLabel(
  item: StatementV2SummaryItem,
  dimension: StatementV2Dimension,
  stripUserPrefix = false
) {
  switch (dimension) {
    case 'user':
      return formatStatementUserLabel(item.user || item.key, stripUserPrefix)
    case 'db':
      return (
        item.schema_name ||
        item.db ||
        item.database ||
        item.dimension ||
        item.key ||
        UNKNOWN_DB_LABEL
      )
    case 'table':
      return (
        item.table_name ||
        item.table ||
        item.dimension ||
        item.key ||
        UNKNOWN_TABLE_LABEL
      )
    case 'sqltypes':
      return item.stmt_type || item.dimension || item.key || UNKNOWN_TYPE_LABEL
    case 'digest':
      return (
        item.sql_text?.trim() ||
        item.query_sample_text?.trim() ||
        item.digest_text?.trim() ||
        item.sql_digest ||
        TOP_SQL_OTHERS_LABEL
      )
  }
}

function getSummaryItemKey(
  item: StatementV2SummaryItem,
  dimension: StatementV2Dimension
) {
  switch (dimension) {
    case 'user':
      return item.user || item.key || '__unknown_user__'
    case 'db':
      return (
        item.schema_name ||
        item.db ||
        item.database ||
        item.dimension ||
        item.key ||
        '__unknown_db__'
      )
    case 'table':
      return (
        item.table_name ||
        item.table ||
        item.dimension ||
        item.key ||
        '__unknown_table__'
      )
    case 'sqltypes':
      return item.stmt_type || item.dimension || item.key || '__unknown_type__'
    case 'digest':
      return (
        item.sql_digest || item.key || item.sql_text || TOP_SQL_OTHERS_LABEL
      )
  }
}

export function buildStatementTrendSeriesFromSummary(
  items: StatementV2SummaryItem[],
  timeRangeValue: [number, number],
  stepSeconds: number,
  dimension: StatementV2Dimension = 'digest',
  stripUserPrefix = false
): StatementTrendSeries[] {
  return items.map((item) => ({
    key: getSummaryItemKey(item, dimension),
    label: getSummaryItemLabel(item, dimension, stripUserPrefix),
    points: fillTrendPointGaps(
      (item.timestamp_sec || []).map((timestamp, index) => ({
        timestamp,
        value: item.value_by_time?.[index] ?? 0
      })),
      stepSeconds,
      timeRangeValue
    )
  }))
}
